import { Switch } from '@headlessui/react';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCaretRight } from 'react-icons/fa';
import {
  IoIosAddCircleOutline,
  IoIosRemoveCircleOutline,
} from 'react-icons/io';
import Select from 'react-select';

export type BusinessIntervalType = {
  start: number;
  end: number;
};

export type BusinessDayType = {
  dayNumber: number;
  dayName: string;
  openings: BusinessIntervalType[];
};

export type SelectedTimeType = {
  value: number;
  label: string;
};

const totalSeconds = 60 * 60 * 24;
const stepSeconds = 60 * 15;

export const getDefaultIntervals = (step: number, h24: boolean) => {
  let position = 0;

  const intervals: SelectedTimeType[] = [];

  while (position < totalSeconds) {
    const hours = Math.floor(position / 3600) % 24;
    const minutes = Math.round((position - hours * 3600) / 60);

    let label = '';

    if (h24) {
      label = `${`${hours}`.padStart(2, '0')}:${`${minutes}`.padStart(2, '0')}`;
    } else if (hours <= 12) {
      label = `${`${hours}`.padStart(2, '0')}:${`${minutes}`.padStart(
        2,
        '0',
      )} AM`;
    } else {
      label = `${`${hours - 12}`.padStart(2, '0')}:${`${minutes}`.padStart(
        2,
        '0',
      )} PM`;
    }

    intervals.push({
      value: position,
      label,
    });

    position += step;
  }

  return intervals;
};

const formatDaySummary = (
  day: BusinessDayType,
  closedLabel: string,
): string => {
  let formattedOpenings = closedLabel;

  day.openings.forEach((opening, index) => {
    const startHours = Math.floor(opening.start / 3600);
    const startMinutes = Math.round((opening.start - startHours * 3600) / 60);

    const endHours = Math.floor(opening.end / 3600);
    const endMinutes = Math.round((opening.end - endHours * 3600) / 60);

    const startLabel = `${`${startHours % 24}`.padStart(
      2,
      '0',
    )}:${`${startMinutes}`.padStart(2, '0')}`;

    const endLabel = `${`${endHours % 24}`.padStart(
      2,
      '0',
    )}:${`${endMinutes}`.padStart(2, '0')}`;

    if (index > 0) {
      formattedOpenings += ` · ${startLabel} - ${endLabel}`;
    } else {
      formattedOpenings = `${startLabel} - ${endLabel}`;
    }
  });

  return formattedOpenings;
};

interface BusinessDayProps {
  day: BusinessDayType;
  onChange: (day: BusinessDayType) => void;
}

const BusinessDay: React.FC<BusinessDayProps> = ({ day, onChange }) => {
  const timeIntervals = getDefaultIntervals(stepSeconds, true);
  const [open, setOpen] = useState<boolean>(false);
  const { t } = useTranslation();

  const onChangeStartValue = (
    value: SelectedTimeType,
    openingIndex: number,
  ) => {
    const newOpenings = day.openings.map((oldOpening, oldOpeningIndex) => {
      if (oldOpeningIndex === openingIndex) {
        if (value.value > oldOpening.end) {
          return {
            start: value.value,
            end: value.value + stepSeconds,
          };
        }
        return {
          start: value.value,
          end: oldOpening.end,
        };
      }

      return oldOpening;
    });

    onChange({
      ...day,
      openings: newOpenings,
    });
  };

  const onChangeEndValue = (value: SelectedTimeType, openingIndex: number) => {
    const newOpenings = day.openings.map((oldOpening, oldOpeningIndex) => {
      if (oldOpeningIndex === openingIndex) {
        return {
          start: oldOpening.start,
          end: value.value,
        };
      }

      return oldOpening;
    });

    onChange({
      ...day,
      openings: newOpenings,
    });
  };

  const onClosedDay = () => {
    onChange({
      ...day,
      openings: [],
    });
    setOpen(false);
  };

  const onOpenDay = () => {
    onChange({
      ...day,
      openings: [{ start: 60 * 60 * 9, end: 60 * 60 * 18 }],
    });
    setOpen(true);
  };

  const onAddInterval = () => {
    onChange({
      ...day,
      openings: [...day.openings, { start: 60 * 60 * 9, end: 60 * 60 * 18 }],
    });
  };

  const onDeleteInterval = (index: number) => {
    if (day.openings.length <= 1) {
      onClosedDay();
    } else {
      const newOpenings = day.openings.filter(
        (_, openingIndex) => openingIndex !== index,
      );

      onChange({
        ...day,
        openings: newOpenings,
      });
    }
  };

  const createSelectLabel = (option: any) => (
    <div className="flex flex-row items-center gap-3">
      <div>{option.label}</div>
    </div>
  );

  return (
    <div className="flex w-full flex-row rounded-xl border-2 bg-white">
      <div
        className={`flex min-h-[56px] flex-col px-2 ${
          open
            ? 'items-start justify-start pt-3'
            : 'items-center justify-center'
        }`}
      >
        <Switch
          data-test-id={`business_day_toggle_${day.dayNumber}`}
          checked={day.openings.length > 0}
          onChange={(value) => {
            if (value) {
              onOpenDay();
            } else {
              onClosedDay();
            }
          }}
          className={`${
            day.openings.length > 0 ? 'bg-sky-600' : 'bg-gray-200'
          } relative inline-flex h-6 w-11 items-center rounded-full`}
        >
          <span
            className={`${
              day.openings.length > 0 ? 'translate-x-6' : 'translate-x-1'
            } inline-block size-4 rounded-full bg-white transition`}
          />
        </Switch>
      </div>
      <div className="group flex min-h-[48px] grow flex-col items-center justify-between">
        <div
          data-test-id="ds15B-I6Uc_09DOdeO8f7"
          onClick={(e) => {
            e.preventDefault();
            setOpen(!open);
            if (!open && day.openings.length <= 0) {
              onOpenDay();
            }
          }}
          onKeyDown={(e) => {
            e.preventDefault();
            setOpen(!open);
            if (!open && day.openings.length <= 0) {
              onOpenDay();
            }
          }}
          role="presentation"
          className={`size-full cursor-pointer ${open && 'pt-3'}`}
        >
          <div className="flex size-full flex-row items-center justify-between">
            <div
              className={`ml-1 mr-4 ${
                day.openings.length > 0 ? 'font-semibold' : 'text-gray-300'
              }`}
            >
              {t(day.dayName)}
            </div>
            <div className="grow">
              {!open && (
                <p className="line-clamp-1 overflow-hidden text-ellipsis break-all text-sm font-semibold text-gray-500">
                  {formatDaySummary(day, t('static.hours.closed_label'))}
                </p>
              )}
            </div>
            <div
              className={`mr-2 flex flex-col items-center justify-center transition-transform ${
                open && 'rotate-90'
              }`}
            >
              <FaCaretRight size={24} />
            </div>
          </div>
        </div>
        <div
          className={`flex w-full flex-col pb-4 ${open ? 'block' : 'hidden'}`}
        >
          {day.openings.map((opening, openingIndex) => {
            const startValue = timeIntervals.filter(
              (interval) => opening.start === interval.value,
            );
            const endValue = timeIntervals.filter(
              (interval) => opening.end === interval.value,
            );

            return (
              <div className="mt-4 flex w-full flex-row py-1 pr-2">
                <div className="flex basis-4/5 flex-col sm:flex-row">
                  <div className="grow basis-2/5">
                    <Select
                      data-test-id="biz-day-selector"
                      placeholder={t('static.generic.selector_placeholder')}
                      isSearchable={false}
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuShouldScrollIntoView
                      closeMenuOnSelect
                      options={timeIntervals.slice(0, timeIntervals.length - 1)}
                      value={startValue}
                      isMulti={false}
                      onChange={(option: SelectedTimeType | null) => {
                        if (option) {
                          onChangeStartValue(option, openingIndex);
                        }
                      }}
                      formatOptionLabel={createSelectLabel}
                      classNames={{
                        control: () => 'p-3 border-2',
                        input: () => 'text-lg',
                        option: () => 'text-lg',
                      }}
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 6,
                        colors: {
                          ...theme.colors,
                          primary: '#083344',
                          primary25: '#ecfeff',
                        },
                      })}
                    />
                  </div>
                  <div className="mx-2 flex items-center justify-center p-1 text-gray-400" />
                  <div className="grow basis-2/5">
                    <Select
                      data-test-id="select-time"
                      placeholder={t('static.generic.selector_placeholder')}
                      isSearchable={false}
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuShouldScrollIntoView
                      closeMenuOnSelect
                      options={
                        startValue.length > 0
                          ? timeIntervals.filter(
                              (interval) =>
                                interval.value > startValue[0].value,
                            )
                          : timeIntervals.slice(1)
                      }
                      value={endValue}
                      onChange={(option: SelectedTimeType | null) => {
                        if (option) {
                          onChangeEndValue(option, openingIndex);
                        }
                      }}
                      formatOptionLabel={createSelectLabel}
                      classNames={{
                        control: () => 'p-3 border-2',
                        input: () => 'text-lg',
                        option: () => 'text-lg',
                      }}
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 6,
                        colors: {
                          ...theme.colors,
                          primary: '#083344',
                          primary25: '#ecfeff',
                        },
                      })}
                    />
                  </div>
                </div>
                <div className="ml-4 flex basis-1/5 flex-row items-center justify-center align-middle">
                  {openingIndex === day.openings.length - 1 && (
                    <div className="flex size-full flex-col items-center justify-evenly sm:flex-row">
                      {openingIndex > 0 && (
                        <div className="basis-1/12 p-1 text-sky-600">
                          <IoIosRemoveCircleOutline
                            data-test-id="p0N3rvb__zfY7htD28WXM"
                            size={30}
                            className="cursor-pointer transition hover:opacity-70"
                            onClick={() => onDeleteInterval(openingIndex)}
                          />
                        </div>
                      )}
                      <div className="basis-1/12 p-1 text-sky-600">
                        <IoIosAddCircleOutline
                          data-test-id="_0fStN6Yq8JSdytf6txBI"
                          size={30}
                          className="cursor-pointer transition hover:opacity-70"
                          onClick={onAddInterval}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BusinessDay;
